/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';

import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatLocalizedDate } from '@/shared/utils/date';

/**
 * The small repeated shapes of the sale detail screen, kept in one place so
 * the card, the micro-label and the pill cannot drift apart across the eight
 * components that use them.
 *
 * Colours come through `Text`'s `tone` prop rather than a colour utility: tone
 * resolves to an inline `--text-*` token, which cannot lose a class-merge race
 * against the default colour `Text` applies when no tone is given.
 */

/**
 * Formats a date-only value without shifting it by a day.
 *
 * The paperwork milestones and commission receipts are stored as calendar
 * dates (`"2026-09-15"`), which `new Date` reads as UTC midnight. Formatting
 * that in any negative-offset timezone — Brazil included — renders the
 * previous day, so the stored date and the displayed date disagree. Forcing
 * UTC makes the display show the calendar date that was actually recorded.
 *
 * @param value - A calendar date string, possibly empty.
 * @returns The localized date, or an empty string when unset or invalid.
 */
export function formatCalendarDate(value: string | undefined | null): string {
  return formatLocalizedDate(value, undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * `MicroLabel` and `SurfaceCard` now live in `@/shared/ui`, where the deals
 * and leases detail screens reach them too. They are re-exported here so the
 * eight sales components importing them from this module keep working; the
 * imports get rewritten in a separate mechanical sweep.
 */
export { MicroLabel, SurfaceCard } from '@/shared/ui';

/**
 * A rounded tinted pill, used for counts and short statuses.
 *
 * @param props.children - The pill text.
 * @param props.accent - The accent token driving text and tint.
 * @param props.className - Extra classes.
 * @returns The rendered pill.
 */
export function Pill({
  children,
  accent,
  className,
}: {
  children: ReactNode;
  accent?: string;
  className?: string;
}) {
  const tinted = accent
    ? { color: accent, background: `color-mix(in srgb, ${accent} 14%, transparent)` }
    : undefined;

  return (
    <Text
      component="span"
      tone={accent ? undefined : 'tertiary'}
      style={tinted}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-(--radius-full)',
        'px-(--space-2) py-(--space-0-5) text-[length:var(--text-meta)] font-medium',
        !accent && 'bg-(--surface-tertiary)',
        className,
      )}
    >
      {children}
    </Text>
  );
}

/**
 * The monospace identifier chip beside a record's title.
 *
 * @param props.children - The identifier, usually shortened for display.
 * @param props.title - The full value, for hovering and copying.
 * @returns The rendered chip.
 */
export function IdentifierChip({
  children,
  title,
}: {
  children: ReactNode;
  title?: string;
}) {
  return (
    <Text
      component="span"
      tone="tertiary"
      title={title}
      className={cn(
        'inline-flex items-center rounded-(--radius-full) border border-(--border-subtle)',
        'px-(--space-2) py-(--space-0-5)',
        'text-[length:var(--text-meta)]',
      )}
    >
      {children}
    </Text>
  );
}

/**
 * The two-tier empty state shared by every panel on the detail screen:
 * a pair of faded skeleton bars, a primary message, and an optional
 * lighter hint explaining why the section is empty rather than broken.
 * Matches the attendance detail screen's `TimelineEmpty`/`ProposalsEmpty`
 * idiom, and is the single copy of it on this screen — `SaleActivityFeed`
 * used to carry its own inline duplicate.
 *
 * @param props.message - The primary empty-state line.
 * @param props.hint - An optional secondary, lighter line.
 * @returns The rendered empty state.
 */
export function PanelEmptyState({
  message,
  hint,
}: {
  message: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-(--space-1) py-(--space-2)">
      <div className="flex flex-col gap-(--space-1-5) opacity-50" aria-hidden="true">
        <div className="h-2 w-[55%] rounded-(--radius-full) bg-(--surface-tertiary)" />
        <div className="h-2 w-[78%] rounded-(--radius-full) bg-(--surface-tertiary)" />
      </div>
      <Text component="span" variant="small" tone="secondary" className="mt-(--space-2)">
        {message}
      </Text>
      {hint && (
        <Text component="span" variant="meta" tone="tertiary" className="leading-relaxed">
          {hint}
        </Text>
      )}
    </div>
  );
}

/**
 * A label/value pair, stacked — the field shape used inside the tab bodies.
 *
 * @param props.label - The field label.
 * @param props.value - The field value, already formatted.
 * @param props.accent - Optional colour token for the value.
 * @returns The rendered field.
 */
export function StackedField({
  label,
  value,
  accent,
}: {
  label: string;
  value: ReactNode;
  accent?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-(--space-0-5)">
      <Text component="span" tone="tertiary" variant="meta">
        {label}
      </Text>
      <Text
        component="span"
        variant="bodySm"
        fw={500}
        tone={accent ? undefined : 'primary'}
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </Text>
    </div>
  );
}
